import './App.css';
import { Suspense } from 'react';
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter
} from 'react-router-dom';

import {
  Wallet,
  Settings,
  AllOrders,
  Footer,
  Landing,
  Login,
  Marketplace,
  TopNav,
  CheckOut,
  Print,
  ErrorPage
} from 'src/page';

import { Loader } from './component';
import ProtectedRoutesProvider, {
  useProtectedRoutesContext
} from './context/ProtectedRoutes';
import { CartProvider } from './context/CartContext';


const ProtectedRoute = () => {
  const { user, token, school } = useProtectedRoutesContext();
  const isAuthenticated = !!user && !!token && !!school;
  return isAuthenticated ? <Outlet /> : <Navigate to="/signin" />;
};


const GuestRoute = () => {
  const { user, token, school } = useProtectedRoutesContext();
  const isAuthenticated = !!user && !!token && !!school;
  return isAuthenticated ? <Navigate to="/" /> : <Outlet />;
};


const PublicLayout = ({ children }) => (
  <div className="overflow-hidden">
    <TopNav />
    <div className="min-h-[80vh] my-[4rem]">{children}</div>
    <Footer />
  </div>
);


const AppLayout = () => (
  <div className="overflow-hidden">
    <TopNav />
    <div className="min-h-[80vh] my-[4rem]">
      <Outlet />
    </div>
    <Footer />
  </div>
);

const AuthLayout = () => (
  <>
    <TopNav />
    <Outlet />
    <Footer />
  </>
);

function App() {
  const router = createBrowserRouter([
    // Public Marketplace Route
    {
      path: '/',
      element: <PublicLayout><Marketplace /></PublicLayout>,
    },

    // Guest-only login routes
    {
      element: <GuestRoute />,
      children: [
        {
          element: <AuthLayout />,
          children: [
            { path: ':code', element: <Login /> },
            { path: 'signin', element: <Login /> },
            { path: 'staff/:code', element: <Login staff /> },
            { path: 'staff', element: <Login staff /> }
          ]
        }
      ]
    },

    // Protected app routes
    {
      element: <ProtectedRoute />,
      children: [
        {
          element: <AppLayout />,
          errorElement: <ErrorPage code="500" />,
          children: [
            { path: 'dashboard', element: <Landing /> },
            { path: 'checkout', element: <CheckOut /> },
            { path: 'wallet', element: <Wallet /> },
            { path: 'orders', element: <AllOrders /> },
            { path: 'settings', element: <Settings /> },
            {
              path: 'account',
              children: [
                { path: 'wallet', element: <Wallet /> },
                { path: 'orders', element: <AllOrders /> },
                { path: 'settings', element: <Settings /> }
              ]
            }
          ]
        }
      ]
    },

    // Utility/Edge routes
    { path: 'print', element: <Print /> },
    { path: '/unauthorized', element: <ErrorPage code="401" /> },
    { path: '*', element: <ErrorPage code="404" /> }
  ]);

  return (
    <ProtectedRoutesProvider>
      <Suspense fallback={<Loader spin />}>
        <CartProvider>
          <RouterProvider router={router} />
        </CartProvider>
      </Suspense>
    </ProtectedRoutesProvider>
  );
}

export default App;
